import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Brain, ImageIcon, FileText, Sparkles, Loader2, Upload, ChevronDown, Download, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

type AIImageGeneratorType = React.FC & { Trigger: React.FC };

const AIImageGenerator: AIImageGeneratorType = (() => {
  const Component: React.FC = () => null;
  const Trigger: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [image, setImage] = useState<string | null>(null);
    const [usageData, setUsageData] = useState<{used_count: number; daily_limit: number} | null>(null);
    const [loadingUsage, setLoadingUsage] = useState(true);
    const { toast } = useToast();

    // Fetch usage data on component mount
    useEffect(() => {
      const fetchUsageData = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // Check if usage record exists
          const { data: existingUsage, error: fetchError } = await supabase
            .from('ai_image_usage_limits')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

          if (fetchError) {
            console.error('Error fetching usage data:', fetchError);
            return;
          }

          if (existingUsage) {
            // Check if we need to reset daily count
            const today = new Date().toISOString().split('T')[0];
            const resetDate = new Date(existingUsage.reset_date).toISOString().split('T')[0];
            
            if (today !== resetDate) {
              // Reset the count for new day
              const { data: updatedUsage, error: resetError } = await supabase
                .from('ai_image_usage_limits')
                .update({ 
                  used_count: 0, 
                  reset_date: today,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', user.id)
                .select()
                .single();

              if (!resetError && updatedUsage) {
                setUsageData(updatedUsage);
              }
            } else {
              setUsageData(existingUsage);
            }
          } else {
            // Create new usage record
            const { data: newUsage, error: createError } = await supabase
              .from('ai_image_usage_limits')
              .insert({
                user_id: user.id,
                used_count: 0,
                daily_limit: 10,
                reset_date: new Date().toISOString().split('T')[0]
              })
              .select()
              .single();

            if (!createError && newUsage) {
              setUsageData(newUsage);
            }
          }
        } catch (error) {
          console.error('Error managing usage data:', error);
        } finally {
          setLoadingUsage(false);
        }
      };

      fetchUsageData();
    }, []);

    const handleGenerate = async () => {
      if (!prompt.trim()) {
        toast({ variant: 'destructive', title: 'Please enter a prompt', description: 'Enter a prompt to generate an image.' });
        return;
      }

      // Check usage limit
      if (usageData && usageData.used_count >= usageData.daily_limit) {
        toast({ 
          variant: 'destructive', 
          title: 'Daily limit reached', 
          description: `You have reached your daily limit of ${usageData.daily_limit} image generations. Try again tomorrow.` 
        });
        return;
      }

      setLoading(true);
      setImage(null);
      try {
        const { data, error } = await supabase.functions.invoke('ai-image-generate', {
          body: { prompt }
        });
        if (error) throw error;
        if (data?.image) {
          setImage(data.image);
          
          // Update usage count
          if (usageData) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const { data: updatedUsage, error: updateError } = await supabase
                .from('ai_image_usage_limits')
                .update({ 
                  used_count: usageData.used_count + 1,
                  updated_at: new Date().toISOString()
                })
                .eq('user_id', user.id)
                .select()
                .single();

              if (!updateError && updatedUsage) {
                setUsageData(updatedUsage);
              }
            }
          }
          
          toast({ title: 'Image generated successfully', description: 'Your session visualization has been created.' });
        } else {
          throw new Error('No image returned from the API');
        }
      } catch (e: any) {
        console.error('AI image generate error:', e);
        let errorMessage = 'Failed to generate image. Please try again.';
        
        if (e?.message?.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (e?.message?.includes('API configuration')) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (e?.message) {
          errorMessage = e.message;
        }
        
        toast({ 
          variant: 'destructive', 
          title: 'Generation failed', 
          description: errorMessage 
        });
      } finally {
        setLoading(false);
      }
    };

    const handleDownload = () => {
      if (!image) return;
      
      try {
        // Create download link
        const link = document.createElement('a');
        link.href = image;
        link.download = `session-visualization-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({ title: 'Download started', description: 'Your image is being downloaded.' });
      } catch (error) {
        console.error('Download error:', error);
        toast({ variant: 'destructive', title: 'Download failed', description: 'Failed to download the image.' });
      }
    };

    const handleRetry = () => {
      handleGenerate();
    };

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="default">Open Image Generator</Button>
        </DialogTrigger>
        <DialogContent className="w-full sm:max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Session Visualization AI</DialogTitle>
            <DialogDescription>Enter a prompt to generate session visualization images.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[calc(90vh-8rem)] overflow-auto pr-1">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., a simple line-art illustration of knee rehab exercises on a white background"
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleGenerate} 
                disabled={loading || loadingUsage || (usageData && usageData.used_count >= usageData.daily_limit)}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating…
                  </>
                ) : loadingUsage ? (
                  'Loading...'
                ) : (
                  `Generate Image ${usageData ? `(${usageData.daily_limit - usageData.used_count}/${usageData.daily_limit} left)` : ''}`
                )}
              </Button>
              <Button variant="outline" onClick={() => { setPrompt(''); setImage(null); }}>Reset</Button>
            </div>
            {image && (
              <div className="border rounded-md p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Generated Image</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retry
                    </Button>
                    <Button variant="default" size="sm" onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="max-h-[70vh] overflow-auto">
                  <img src={image} alt="session visualization" className="w-full h-auto object-contain rounded" loading="lazy" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };
  (Component as any).Trigger = Trigger;
  return Component as AIImageGeneratorType;
})();

interface Transcript {
  id: string;
  transcript_text: string;
  created_at: string;
  recording_duration_seconds: number;
  ai_analysis?: any;
}

interface AIToolsTabProps {
  booking: {
    id: string;
    client_id: string;
    practitioner_id: string;
    start_time: string;
    end_time: string;
    status: string;
    format: string;
    clients?: {
      first_name: string;
      last_name: string;
    };
    practitioners?: {
      first_name: string;
      last_name: string;
    };
  };
}

export function AIToolsTab({ booking }: AIToolsTabProps) {
  const { toast } = useToast();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [selectedTranscript, setSelectedTranscript] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch transcripts for this booking
  useEffect(() => {
    const fetchTranscripts = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('session_transcripts')
          .select('*')
          .eq('booking_id', booking.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTranscripts(data || []);
      } catch (error) {
        console.error('Error fetching transcripts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranscripts();
  }, [booking.id]);

  // Generate session notes from selected transcript
  const generateSessionNotes = async (format: string = 'soap') => {
    if (!selectedTranscript) {
      toast({
        title: "No Transcript Selected",
        description: "Please select a transcript to analyze",
        variant: "destructive"
      });
      return;
    }

    const transcript = transcripts.find(t => t.id === selectedTranscript);
    if (!transcript) return;

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-client-summary', {
        body: {
          notes: [transcript.transcript_text],
          format: format,
          language: 'en'
        }
      });

      if (error) throw error;
      
      const generatedText = data.generatedText || data.summary || '';
      
      // Download the generated notes
      const formatLabel = format.toUpperCase();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${formatLabel}_Notes_${timestamp}.txt`;
      
      const blob = new Blob([generatedText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: "Notes Generated",
        description: `${formatLabel} notes generated and downloaded successfully`,
      });
    } catch (error) {
      console.error('Error generating notes:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate session notes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const aiTools = [
    {
      id: 'session-assistant',
      title: 'Session Assistant AI',
      description: 'Transform session transcripts into structured session notes with SOAP format and intelligent analysis',
      icon: Brain,
      features: [
        'SOAP note generation',
        'Transcript analysis',
        'Session summaries',
        'Multiple formats'
      ]
    },
    {
      id: 'image-generation',
      title: 'Session Visualization AI',
      description: 'Generate visual aids and progress charts for client sessions',
      icon: ImageIcon,
      features: [
        'Progress visualizations',
        'Anatomical diagrams',
        'Therapeutic exercise illustrations',
        'Recovery milestone charts'
      ]
    },
  ];

  return (
    <div className="space-y-6 pb-4">
      <div className="text-center py-6">
        <Sparkles className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl font-bold mb-2">AI-Powered Session Tools</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Advanced AI tools to enhance your practice with intelligent session analysis, 
          automated documentation, and visual session aids.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          ⚠️ AI tools assist practitioners with documentation only. They do not provide independent diagnosis or treatment.
        </p>
      </div>
      <div className="grid gap-6">
        {aiTools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <Card key={tool.id} className="relative overflow-hidden">
              
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <IconComponent className="h-6 w-6 text-primary" />
                  </div>
                  {tool.title}
                </CardTitle>
                <CardDescription className="text-base">
                  {tool.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Key Features
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {tool.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  {tool.id === 'image-generation' && (
                    <div className="pt-3">
                      <AIImageGenerator.Trigger />
                    </div>
                  )}
                  {tool.id === 'session-assistant' && (
                    <div className="pt-3 space-y-3">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span className="text-sm">Loading transcripts...</span>
                        </div>
                      ) : transcripts.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No transcripts available</p>
                          <p className="text-xs">Record a session first or upload a transcript</p>
                          <Button variant="outline" size="sm" className="mt-2">
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Transcript
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Select Transcript</label>
                            <Select value={selectedTranscript} onValueChange={setSelectedTranscript}>
                              <SelectTrigger className="bg-background">
                                <SelectValue placeholder="Choose a transcript to analyze" />
                              </SelectTrigger>
                              <SelectContent className="bg-background border z-50">
                                {transcripts.map((transcript) => (
                                  <SelectItem key={transcript.id} value={transcript.id}>
                                    <div className="flex items-center justify-between w-full">
                                      <span className="text-sm">
                                        {new Date(transcript.created_at).toLocaleDateString()} - 
                                        {formatDuration(transcript.recording_duration_seconds)}
                                      </span>
                                      {transcript.ai_analysis && (
                                        <Badge variant="secondary" className="ml-2">Analyzed</Badge>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Generate & Download</label>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  className="w-full justify-between bg-background"
                                  disabled={!selectedTranscript || isGenerating}
                                >
                                  {isGenerating ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex items-center">
                                        <Brain className="h-4 w-4 mr-2" />
                                        Generate Session Notes
                                      </div>
                                      <ChevronDown className="h-4 w-4" />
                                    </>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-56 bg-background border z-50">
                                <DropdownMenuItem onClick={() => generateSessionNotes('soap')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  <div>
                                    <div className="font-medium">SOAP Notes</div>
                                    <div className="text-xs text-muted-foreground">Subjective, Objective, Assessment, Plan</div>
                                  </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => generateSessionNotes('dap')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  <div>
                                    <div className="font-medium">DAP Notes</div>
                                    <div className="text-xs text-muted-foreground">Data, Assessment, Plan</div>
                                  </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => generateSessionNotes('birp')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  <div>
                                    <div className="font-medium">BIRP Notes</div>
                                    <div className="text-xs text-muted-foreground">Behavior, Intervention, Response, Plan</div>
                                  </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => generateSessionNotes('narrative')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  <div>
                                    <div className="font-medium">Narrative Notes</div>
                                    <div className="text-xs text-muted-foreground">Free-form documentation</div>
                                  </div>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

    </div>
  );
}